import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, STATUS_LABELS, PROJECT_STATUSES } from "../lib/api";
import { useNavigate } from "react-router-dom";
import StatusBadge from "../components/StatusBadge";
import { Plus, Search, Briefcase, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAll = () => {
    setLoading(true);
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  };
  useEffect(fetchAll, []);

  const canCreate = user && ["admin", "chef_projet"].includes(user.role);

  const filtered = projects.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (filter && !(`${p.name} ${p.code} ${p.client}`.toLowerCase().includes(filter.toLowerCase()))) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Portefeuille</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Projets</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} projet{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowModal(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-4 h-10 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="create-project-btn">
            <Plus size={16} /> Nouveau projet
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher par nom, code, client…" className="w-full h-10 pl-10 pr-4 text-sm border border-slate-300 rounded-md bg-white focus:border-btp-navy focus:outline-none" data-testid="project-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white" data-testid="project-status-filter">
          <option value="all">Tous les statuts</option>
          {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-slate-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-16 text-center">
          <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="font-heading text-lg font-semibold">Aucun projet</h3>
          <p className="text-sm text-slate-500 mt-1">Créez votre premier projet pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
              className="text-left bg-white border border-slate-200 rounded-md p-5 hover:border-btp-navy hover:shadow-md transition-all"
              data-testid={`project-card-${p.code}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="font-mono text-[11px] text-slate-500">{p.code}</div>
                <StatusBadge status={p.status} />
              </div>
              <h3 className="font-heading text-base font-bold leading-snug line-clamp-2 mb-1">{p.name}</h3>
              <div className="text-xs text-slate-500 mb-4">{p.client} · {p.country}</div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                    <span>Avancement</span><span className="font-mono text-slate-700">{Math.round((p.progress || 0) * 100)}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-btp-navy" style={{ width: `${(p.progress || 0) * 100}%` }}></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-100">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Budget</div>
                    <div className="font-mono text-slate-700">{fmtMoney(p.budget_prevu)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Échéance</div>
                    <div className="font-mono text-slate-700">{fmtDate(p.end_date)}</div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showModal && <CreateProjectModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); fetchAll(); }} />}
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", client: "", description: "", country: "Sénégal", status: "opportunite",
    start_date: "", end_date: "", budget_prevu: 0, tdr_text: "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/projects", { ...form, budget_prevu: Number(form.budget_prevu) });
      toast.success("Projet créé");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-project-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-xl font-bold">Nouveau projet</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-modal"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom du projet" required>
              <input required value={form.name} onChange={update("name")} className="input-base" data-testid="project-name-input" />
            </Field>
            <Field label="Client" required>
              <input required value={form.client} onChange={update("client")} className="input-base" data-testid="project-client-input" />
            </Field>
            <Field label="Pays">
              <input value={form.country} onChange={update("country")} className="input-base" />
            </Field>
            <Field label="Statut">
              <select value={form.status} onChange={update("status")} className="input-base">
                {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </Field>
            <Field label="Date début">
              <input type="date" value={form.start_date} onChange={update("start_date")} className="input-base" />
            </Field>
            <Field label="Date fin">
              <input type="date" value={form.end_date} onChange={update("end_date")} className="input-base" />
            </Field>
            <Field label="Budget prévu ($)">
              <input type="number" min="0" value={form.budget_prevu} onChange={update("budget_prevu")} className="input-base" data-testid="project-budget-input" />
            </Field>
          </div>
          <Field label="Description">
            <textarea rows={3} value={form.description} onChange={update("description")} className="input-base" />
          </Field>
          <Field label="TDR (texte intégral, facultatif – pour analyse IA)">
            <textarea rows={4} value={form.tdr_text} onChange={update("tdr_text")} className="input-base font-mono text-xs" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 h-10 bg-btp-terra hover:bg-btp-terra-hover text-white rounded-md text-sm font-semibold flex items-center justify-center gap-2" data-testid="submit-create-project">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Créer"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input-base{display:block;width:100%;height:40px;padding:0 0.75rem;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;background:#fff;outline:none;transition:border-color .15s} .input-base:focus{border-color:#0A2540;box-shadow:0 0 0 1px #0A2540} textarea.input-base{height:auto;padding:0.5rem 0.75rem;font-family:'IBM Plex Sans',sans-serif}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">
        {label} {required && <span className="text-btp-terra">*</span>}
      </div>
      {children}
    </label>
  );
}
